// DynamicFormRenderer_Helix.js
import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle, useCallback } from "react";
// import { Flex } from "@digitallabs/one-x-ui";
import { Flex } from "../../resourses/digitallabs/one-x-ui";
import "../../styles/DynamicFormRenderer_Helix.css";

import { validateFieldRules, extractMaxLength, formatToDDMMYYYY, parseDDMMYYYY } from "./validationRules";
import { getDMQMedicalConditions, getStoredFormData } from "../../utils/formStorage";
import { DMQStepNames, MAIN_STEPS } from "../../constants/constants";
import { countries_metaData } from "../../services/serviceCollections/countriesService";
import { handlePincodeLookup } from "../../utils/pincodeHandler";
import { useLoader } from "../../context/LoaderContext";

import DynamicInputPincode from "./dynamicComponents/dynamicInputPincode";
import DynamicInputStateCity from "./dynamicComponents/dynamicInputStateCity";
import DynamicLabel from "./dynamicComponents/dynamicLabel";
import DynamicSelect from "./dynamicComponents/dynamicSelect";
import DynamicSelectCountry from "./dynamicComponents/dynamicSelectCountry";
import DynamicSelectIndianState from "./dynamicComponents/dynamicSelectIndianState";
import DynamicSelectYearpicker from "./dynamicComponents/dynamicSelectYearpicker";
import DynamicCheckboxMultiselect from "./dynamicComponents/dynamicCheckboxMultiselect";
import DynamicCheckbox from "./dynamicComponents/dynamicCheckbox";
import DynamicChips from "./dynamicComponents/dynamicChips";
import DynamicMedicationMultiple from "./dynamicComponents/DynamicMedicationMultiple";
import DynamicTextArea from "./dynamicComponents/dynamicTextArea";
import DynamicDatepicker from "./dynamicComponents/dynamicDatepicker";
import DynamicInputHeightConvertor from "./dynamicComponents/dynamicInputHeightConvertor";
import { indianStates } from "../../services/serviceCollections/indianStateService";

// Normalize select list
const normalizeItems = (validAnswers) => {
  if (!Array.isArray(validAnswers)) return [];
  return validAnswers.map((it) => ({
    label: typeof it === "string" ? it : it.label,
    value: typeof it === "string" ? it : it.value,
  }));
};

const normalizeCountryOptions = (countries) => {
  if (!Array.isArray(countries)) return [];

  return countries.map((c) => ({
    label: c.country_name,
    value: c.country_name, // <-- we store ONLY country_name
  }));
};

const normalizeIndianStateOptions = (states) => {
  if (!Array.isArray(states)) return [];

  return states.map((s) => ({
    label: s.state_name,
    value: s.state_name, // <-- we store ONLY state_name
  }));
};

const normalizeYearValues = (dob) => {
  const currentYear = new Date().getFullYear();
  let startYear = currentYear;
  let endYear;

  let dobYear;

  if (dob) {
    const parts = dob.split("/");
    if (parts.length === 3) {
      const [day, month, year] = parts;
      dobYear = Number(year);
    } else {
      dobYear = new Date(dob).getFullYear();
    }

    if (!isNaN(dobYear)) {
      endYear = dobYear;
    } else {
      endYear = currentYear - 60;
    }
  } else {
    endYear = currentYear - 60;
  }

  const years = [];
  for (let y = startYear; y >= endYear; y--) {
    years.push({
      label: String(y),
      value: String(y),
    });
  }

  return years;
};

const normalizeCurrentAndNextYear = () => {
  const currentYear = new Date().getFullYear();

  return [
    {
      label: String(currentYear),
      value: String(currentYear),
    },
    {
      label: String(currentYear + 1),
      value: String(currentYear + 1),
    },
  ];
};

const normalizeSelectValue = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return [String(value)];
};

const DynamicFormRenderer_Helix = forwardRef(
  ({ step, internalStep, schema = [], formState = {}, handleChange, onFormFinish, onFormValidityChange }, ref) => {
    const safeState = formState || {};
    const { setLoading } = useLoader();
    const [errors, setErrors] = useState({});
    const [isValid, setIsValid] = useState(false);
    const [submitted, setSubmitted] = useState(false); // <-- only show errors after Next pressed
    const fieldRefs = useRef({});
    const lastNotifiedValidity = useRef(null);
    const stored = getStoredFormData();
    const storedConditions = getDMQMedicalConditions();
    const isUserNRI = storedConditions.isUserNRI;
    const isUserFN = storedConditions.isUserFN;

    const currentGender = stored[DMQStepNames.PERSONAL_DETAILS]?.gender ? stored[DMQStepNames.PERSONAL_DETAILS]?.gender : "Female";
    const currentDOB = stored[DMQStepNames.PERSONAL_DETAILS]?.dob ? stored[DMQStepNames.PERSONAL_DETAILS]?.dob : "";
    const nationality = safeState["nationality"];
    const residentialStatus = safeState["residentialStatus"];
    const residenceCountry = safeState["residenceCountry"];
    const residencePincode = safeState["pincodeResidence"];
    const stateResidence = safeState["stateResidence"];
    const cityResidence = safeState["cityResidence"];
    const workPincode = safeState["pincodeWork"];
    const stateWork = safeState["stateWork"];
    const cityWork = safeState["cityWork"];

    const [pincodeLocks, setPincodeLocks] = useState({
      residence: { state: false, city: false },
      work: { state: false, city: false },
    });

    const [pincodeAutoFilled, setPincodeAutoFilled] = useState({
      residence: false,
      work: false,
    });

    const [multiPincodeOptions, setMultiPincodeOptions] = useState({
      residence: null,
      work: null,
    });

    const updatePincodeLock = useCallback((type, nextLock) => {
      setPincodeLocks((prev) => {
        const current = prev[type];
        if (current?.state === nextLock.state && current?.city === nextLock.city) return prev;
        return {
          ...prev,
          [type]: nextLock,
        };
      });
    }, []);

    const updatePincodeAutoFilled = useCallback((type, nextValue) => {
      setPincodeAutoFilled((prev) => {
        if (prev[type] === nextValue) return prev;
        return {
          ...prev,
          [type]: nextValue,
        };
      });
    }, []);

    const updateMultiPincodeOptions = useCallback((type, nextValue) => {
      setMultiPincodeOptions((prev) => {
        if (Object.is(prev[type], nextValue)) return prev;
        return {
          ...prev,
          [type]: nextValue,
        };
      });
    }, []);

    const syncValidity = useCallback(
      (valid) => {
        setIsValid((prev) => (prev === valid ? prev : valid));

        if (typeof onFormValidityChange === "function" && lastNotifiedValidity.current !== valid) {
          lastNotifiedValidity.current = valid;
          onFormValidityChange(valid);
        }
      },
      [onFormValidityChange]
    );

    // Write default answers into state once on schema load
    useEffect(() => {
      schema.forEach((field) => {
        if (field.default_answer !== undefined && field.default_answer !== null) {
          if (safeState[field.name] !== field.default_answer) {
            handleChange(field.name, field.default_answer);
          }
        }
        // also for sub_questions recursively
        if (Array.isArray(field.sub_questions) && field.sub_questions.length) {
          field.sub_questions.forEach((sq) => {
            if (sq.default_answer !== undefined && sq.default_answer !== null) {
              if (safeState[sq.name] !== sq.default_answer) {
                handleChange(sq.name, sq.default_answer);
              }
            }
          });
        }
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schema]);

    /* ---------------- Helpers ---------------- */

    const shouldRenderByGender = (field) => {
      if (!Array.isArray(field.rules)) return true;
      if (field.rules.includes("genderFemaleOnly")) {
        return currentGender === "Female";
      }
      return true;
    };

    const isFieldVisible = (field) => {
      const nationality = safeState["nationality"];
      const residentialStatus = safeState["residentialStatus"];

      // residentialStatus visibility
      if (field.name === "residentialStatus") {
        return nationality === "Indian";
      }

      // residenceCountry visibility
      if (field.name === "residenceCountry") {
        if (nationality === "Non-Indian/OCI/PIO") return true;
        if (nationality === "Indian" && residentialStatus === "NRI") return true;
        return false;
      }

      // generic parent logic
      if (!field.parent) return true;

      return safeState[field.parent] === field.parentValue;
    };

    // Clear subtree values AND errors when hiding children
    const resetSubtree = (subQuestions) => {
      if (!Array.isArray(subQuestions)) return;
      subQuestions.forEach((sq) => {
        // clear value in parent state
        if (safeState[sq.name] !== "") {
          handleChange(sq.name, "");
        }
        // clear errors for that field and its subtree
        setErrors((prev) => {
          if (!Object.prototype.hasOwnProperty.call(prev, sq.name)) return prev;
          const copy = { ...prev };
          delete copy[sq.name];
          return copy;
        });
        if (sq.sub_questions) resetSubtree(sq.sub_questions);
      });
    };

    /* ---------------- Validation logic ---------------- */

    // Decide whether a field is required (supports both schema style and rules)
    const fieldIsRequired = (f) => {
      const rules = Array.isArray(f.rules) ? f.rules : [];
      return f.is_mandatory === "yes" || rules.includes("mandatory");
    };

    // Validate a single field only (used onBlur)
    const validateSingleField = (field) => {
      const newErrors = {};
      if (!field || !field.name) return newErrors;

      // Skip if hidden due to parent/visibility or gender
      if (!shouldRenderByGender(field) || !isFieldVisible(field)) return newErrors;

      // If this field is a radio child marked with _parentIsRadio and parent isn't Yes/Under Medication/(NRI) => skip
      if (field._parentIsRadio) {
        const parentVal = safeState[field._parentName];
        if (!(parentVal === "Yes" || parentVal === "Under Medication" || parentVal === "NRI")) return newErrors;
      }

      const val = safeState[field.name];
      const isEmpty = val === undefined || val === null || (typeof val === "string" && val.trim() === "") || (Array.isArray(val) && val.length === 0);

      // required check
      if (fieldIsRequired(field) && isEmpty) {
        newErrors[field.name] = "This field is required";
        return newErrors;
      }

      // If empty and not required -> valid
      if (isEmpty) return newErrors;

      // Use validateFieldRules for further rule checks
      let fieldToValidate = field;

      //  OVERRIDE RULES FOR NRI PINCODE
      if (isUserNRI && (field.name === "pincodeResidence" || field.name === "pincodeWork")) {
        fieldToValidate = {
          ...field,
          rules: ["mandatory", "numeric", "maxLength:10"],
        };
      }

      if (isUserFN && (field.name === "pincodeResidence" || field.name === "pincodeWork")) {
        fieldToValidate = {
          ...field,
          rules: ["mandatory", "numeric", "maxLength:10"],
        };
      }

      const err = validateFieldRules(val, fieldToValidate);

      if (err) newErrors[field.name] = err;

      return newErrors;
    };

    // Full visible validation (used on Next / to compute overall validity)
    const validateVisible = () => {
      const newErrors = {};

      const walk = (fields, parentVisible = true) => {
        fields.forEach((f) => {
          if (!shouldRenderByGender(f)) return;

          const name = f.name;
          let visible = parentVisible && isFieldVisible(f);

          // Radio child visibility
          if (f._parentIsRadio) {
            if (f.name === "residenceCountry") {
              const nationality = safeState["nationality"];
              const residentialStatus = safeState["residentialStatus"];

              visible = visible && (nationality === "Non-Indian/OCI/PIO" || (nationality === "Indian" && residentialStatus === "NRI"));
            } else {
              visible =
                visible &&
                (safeState[f._parentName] === "Yes" || safeState[f._parentName] === "Under Medication" || safeState[f._parentName] === "NRI");
            }
          }

          if (visible && name) {
            const rawVal = safeState[name];

            /* =====================================================
               MULTIPLE OPTIONS VALIDATION
            ===================================================== */
            if (f.component === "multiple_options") {
              const items = Array.isArray(rawVal) ? rawVal : [];
              const required = fieldIsRequired(f);

              if (required && items.length === 0) {
                newErrors[name] = {
                  0: ["This field is required"],
                };
              } else {
                const fieldErrors = {};

                items.forEach((item, itemIndex) => {
                  const cardErrors = [];

                  (f.sub_fields || []).forEach((subField) => {
                    // Composite fields
                    if (subField.component === "composite") {
                      (subField.fields || []).forEach((innerField) => {
                        const value = item?.[innerField.name];

                        const isEmpty = value === undefined || value === null || (typeof value === "string" && value.trim() === "");

                        if (fieldIsRequired(innerField) && isEmpty) {
                          cardErrors.push(`${innerField.question} is required`);
                        } else if (!isEmpty) {
                          const err = validateFieldRules(value, innerField);

                          if (err) {
                            cardErrors.push(err);
                          }
                        }
                      });
                    }

                    // Normal fields
                    else {
                      const value = item?.[subField.name];

                      const isEmpty = value === undefined || value === null || (typeof value === "string" && value.trim() === "");

                      if (fieldIsRequired(subField) && isEmpty) {
                        cardErrors.push(`${subField.question} is required`);
                      } else if (!isEmpty) {
                        const err = validateFieldRules(value, subField);

                        if (err) {
                          cardErrors.push(err);
                        }
                      }
                    }
                  });

                  if (cardErrors.length > 0) {
                    fieldErrors[itemIndex] = [...new Set(cardErrors)];
                  }
                });

                if (Object.keys(fieldErrors).length > 0) {
                  newErrors[name] = fieldErrors;
                }
              }
            } else {
              /* =====================================================
               EXISTING VALIDATION
            ===================================================== */
              const empty =
                rawVal === undefined ||
                rawVal === null ||
                (typeof rawVal === "string" && rawVal.trim() === "") ||
                (Array.isArray(rawVal) && rawVal.length === 0);

              const required = fieldIsRequired(f);

              if (required && empty) {
                newErrors[name] = "This field is required";
              } else if (!empty) {
                let fieldToValidate = f;

                // NRI PINCODE OVERRIDE
                if (isUserNRI && (f.name === "pincodeResidence" || f.name === "pincodeWork")) {
                  fieldToValidate = {
                    ...f,
                    rules: ["mandatory", "alphaNumeric", "maxLength:10"],
                  };
                }

                if (isUserFN && (f.name === "pincodeResidence" || f.name === "pincodeWork")) {
                  fieldToValidate = {
                    ...f,
                    rules: ["mandatory", "alphaNumeric", "maxLength:10"],
                  };
                }

                const err = validateFieldRules(rawVal, fieldToValidate);

                if (err) {
                  newErrors[name] = err;
                }
              }
            }
          }

          /* =====================================================
             CHILDREN RECURSION
          ===================================================== */

          if (f.component === "radio" && Array.isArray(f.sub_questions)) {
            const childVisible =
              visible &&
              (f.name === "nationality"
                ? safeState[f.name] === "Indian" || safeState[f.name] === "Non-Indian/OCI/PIO"
                : safeState[f.name] === "Yes" || safeState[f.name] === "Under Medication" || safeState[f.name] === "NRI");

            const children = f.sub_questions.map((c) => ({
              ...c,
              _parentIsRadio: true,
              _parentName: f.name,
            }));

            walk(children, childVisible);
          } else if (f.component === "checkbox" && Array.isArray(f.sub_questions)) {
            const isChecked = !!safeState[f.name];

            walk(f.sub_questions, visible && isChecked);
          } else if (f.component === "select" && Array.isArray(f.sub_questions)) {
            const selectedValues = Array.isArray(safeState[f.name]) ? safeState[f.name] : [];

            const visibleChildren = f.sub_questions.filter((sub) => {
              if (!sub.answer_selected) return true;

              return selectedValues.includes(sub.answer_selected);
            });

            walk(visibleChildren, visible);
          } else if (f.sub_questions?.length) {
            walk(f.sub_questions, visible);
          }
        });
      };

      walk(schema, true);

      return newErrors;
    };

    /* ---------------- State sync & parent notification ---------------- */

    // Keep overall validity in sync and notify parent
    useEffect(() => {
      const validation = validateVisible();
      const valid = Object.keys(validation).length === 0;

      syncValidity(valid);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schema, safeState]);

    /* ---------------- Value update ---------------- */

    const updateValue = (name, value) => {
      // Update parent form state
      handleChange(name, value);

      // Clear a single field error when user types/selects
      setErrors((prev) => {
        if (!Object.prototype.hasOwnProperty.call(prev, name)) return prev;
        const copy = { ...prev };
        delete copy[name];
        return copy;
      });

      // Recompute validity and notify parent
      const validation = validateVisible();
      const valid = Object.keys(validation).length === 0;
      syncValidity(valid);
    };

    /* ---------------- Submit / Next handler ---------------- */

    const scrollToFirstError = (errs) => {
      const firstKey = Object.keys(errs)[0];
      if (!firstKey) return;
      const ref = fieldRefs.current[firstKey];
      if (ref && ref.scrollIntoView) {
        ref.scrollIntoView({ behavior: "smooth", block: "center" });
      } else if (ref && ref.current && ref.current.scrollIntoView) {
        ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    };

    const onNext = () => {
      setSubmitted(true);

      const validation = validateVisible();
      setErrors(validation);
      console.log("validation", validation);
      if (Object.keys(validation).length > 0) {
        scrollToFirstError(validation);
        return;
      }

      onFormFinish(safeState);
    };

    useImperativeHandle(ref, () => ({
      triggerNext: () => {
        onNext();
      },
      // expose current validity if parent wants to query
      isFormValid: () => isValid,
    }));

    /* --------------------------------------------------
       Render helpers
    -------------------------------------------------- */

    const renderLabelWithPrefix = (isMain, fieldQuestion) => (
      <div style={{ display: "flex", alignItems: "flex-start", gap: "6px" }}>
        {step === MAIN_STEPS.CONFIRM_DETAILS || step === MAIN_STEPS.PERSONAL_DETAILS
          ? null
          : isMain && (
              <img
                src="/form_bullet.png"
                alt=""
                style={{ height: "10px", marginTop: "4px" }} // adjust marginTop if needed
              />
            )}
        <span style={{ lineHeight: "1.4" }}>{fieldQuestion}</span>
      </div>
    );

    const triggerPincodeIfValid = async (pincode, fieldName, type) => {
      if (isUserNRI) return;
      if (isUserFN) return;
      if (pincode?.length !== 6) return;

      try {
        setLoading(true);

        // Reset previous lookup state
        updatePincodeAutoFilled(type, false);

        updateMultiPincodeOptions(type, null);

        updatePincodeLock(type, {
          state: false,
          city: false,
        });

        const result = await handlePincodeLookup({
          pincode,
          isUserNRI,
          isUserFN,
          type,
          updateValue,
        });

        // -----------------------
        // CASE 1 → Single State + City (Autofill)
        // -----------------------
        if (result?.success && !result?.multipleOptions) {
          updatePincodeAutoFilled(type, true);

          updatePincodeLock(type, {
            state: true,
            city: true,
          });

          updateMultiPincodeOptions(type, null);

          return;
        }

        // -----------------------
        // CASE 2 / CASE 3 → Multiple options
        // -----------------------
        if (result?.multipleOptions) {
          const stateData = result.data;

          // CASE 3 → Single state + multiple cities
          if (stateData?.state && stateData?.cities?.length > 1) {
            updateValue(type === "residence" ? "stateResidence" : "stateWork", stateData.state);

            updatePincodeLock(type, {
              state: true,
              city: false,
            });

            updateMultiPincodeOptions(type, stateData.cities);

            return;
          }

          // CASE 2 → Multiple states (future-proof)
          updatePincodeLock(type, {
            state: false,
            city: false,
          });

          updateMultiPincodeOptions(type, stateData);

          return;
        }

        // -----------------------
        // CASE 4 → No result / manual entry
        // -----------------------
        updatePincodeAutoFilled(type, false);

        updatePincodeLock(type, {
          state: false,
          city: false,
        });

        updateMultiPincodeOptions(type, null);
      } finally {
        setLoading(false);
      }
    };

    const renderField = (field, index = null) => {
      if (!field || !field.name) return null;

      const fieldName = field.name;
      const value = safeState[fieldName] ?? "";

      // skip gender-hidden
      if (!shouldRenderByGender(field)) return null;
      if (!isFieldVisible(field)) return null;

      if (!fieldRefs.current[fieldName]) fieldRefs.current[fieldName] = React.createRef();

      const isMain = index !== null;
      const maxLength = extractMaxLength ? extractMaxLength(field.rules) : undefined;

      /* ---------- LABEL ---------- */
      if (field.component === "label" || field.component === "label_with_bullet") {
        return (
          <Flex key={field.name} direction="column" gap="xs" className="dfh-field-wrapper" ref={(el) => (fieldRefs.current[field.name] = el)}>
            {/* Label */}
            <DynamicLabel
              field={field}
              fieldRefs={fieldRefs}
              value={value}
              maxLength={maxLength}
              isMain={isMain}
              renderLabelWithPrefix={renderLabelWithPrefix}
              step={step}
            />

            {/* Sub Questions (same pattern as DynamicChips) */}
            {Array.isArray(field.sub_questions) &&
              field.sub_questions.map((sub, i) => (
                <Flex
                  key={sub.name ?? `${field.name}-sub-${i}`}
                  direction="column"
                  style={{
                    marginTop: "12px",
                    marginLeft: isMain ? "12px" : "0px",
                  }}
                >
                  {renderField(sub)}
                </Flex>
              ))}
          </Flex>
        );
      }

      /* ---------- input / textarea ---------- */
      if (field.component === "input" && (field.name === "pincodeResidence" || field.name === "pincodeWork")) {
        return (
          <DynamicInputPincode
            key={field.name}
            field={field}
            fieldRefs={fieldRefs}
            value={value}
            isMain={isMain}
            isUserNRI={isUserNRI}
            isUserFN={isUserFN}
            updateValue={updateValue}
            submitted={submitted}
            errors={errors}
            setPincodeLocks={setPincodeLocks}
            setMultiPincodeOptions={setMultiPincodeOptions}
            renderLabelWithPrefix={renderLabelWithPrefix}
            triggerPincodeIfValid={triggerPincodeIfValid}
          />
        );
      }

      /* ---------- GENERIC INPUT  (ALL OTHER FIELDS) ---------- */
      if (field.component === "input") {
        return (
          <DynamicInputStateCity
            key={field.name}
            field={field}
            fieldName={field.name}
            value={value}
            isMain={isMain}
            step={step}
            fieldRefs={fieldRefs}
            pincodeLocks={pincodeLocks}
            multiPincodeOptions={multiPincodeOptions}
            safeState={safeState}
            updateValue={updateValue}
            renderLabelWithPrefix={renderLabelWithPrefix}
            fieldIsRequired={fieldIsRequired}
            normalizeSelectValue={normalizeSelectValue}
            validateSingleField={validateSingleField}
            validateVisible={validateVisible}
            setErrors={setErrors}
            setIsValid={setIsValid}
            onFormValidityChange={onFormValidityChange}
            submitted={submitted}
            errors={errors}
            maxLength={maxLength}
          />
        );
      }

      /* ---------- HEIGHT INPUT CMS TO FEET INCHES ---------- */
      if (field.component === "inputHeightConvertor") {
        return (
          <DynamicInputHeightConvertor
            key={field.name}
            field={field}
            fieldName={field.name}
            value={value}
            isMain={isMain}
            step={step}
            fieldRefs={fieldRefs}
            updateValue={updateValue}
            renderLabelWithPrefix={renderLabelWithPrefix}
            fieldIsRequired={fieldIsRequired}
            validateSingleField={validateSingleField}
            validateVisible={validateVisible}
            setErrors={setErrors}
            setIsValid={setIsValid}
            onFormValidityChange={onFormValidityChange}
            submitted={submitted}
            errors={errors}
            maxLength={maxLength}
          />
        );
      }

      /* ----------  TEXTAREA  ---------- */
      if (field.component === "textarea") {
        return (
          <DynamicTextArea
            key={field.name}
            field={field}
            fieldName={field.name}
            value={value}
            isMain={isMain}
            step={step}
            fieldRefs={fieldRefs}
            updateValue={updateValue}
            renderLabelWithPrefix={renderLabelWithPrefix}
            fieldIsRequired={fieldIsRequired}
            validateSingleField={validateSingleField}
            validateVisible={validateVisible}
            setErrors={setErrors}
            setIsValid={setIsValid}
            onFormValidityChange={onFormValidityChange}
            submitted={submitted}
            errors={errors}
            maxLength={maxLength}
          />
        );
      }

      /* ---------- SELECT ---------- */
      if (field.component === "select") {
        return (
          <DynamicSelect
            key={field.name}
            field={field}
            fieldName={field.name}
            value={value}
            isMain={isMain}
            step={step}
            fieldRefs={fieldRefs}
            normalizeItems={normalizeItems}
            normalizeSelectValue={normalizeSelectValue}
            updateValue={updateValue}
            renderLabelWithPrefix={renderLabelWithPrefix}
            renderField={renderField}
            resetSubtree={resetSubtree}
            safeState={safeState}
            fieldIsRequired={fieldIsRequired}
            validateSingleField={validateSingleField}
            validateVisible={validateVisible}
            setErrors={setErrors}
            setIsValid={setIsValid}
            onFormValidityChange={onFormValidityChange}
            submitted={submitted}
            errors={errors}
          />
        );
      }

      /* ---------- COUNTRY SELECTION (NEW) ---------- */
      if (field.component === "countrySelection") {
        return (
          <DynamicSelectCountry
            key={field.name}
            field={field}
            fieldName={field.name}
            value={value}
            isMain={isMain}
            fieldRefs={fieldRefs}
            normalizeCountryOptions={normalizeCountryOptions}
            normalizeSelectValue={normalizeSelectValue}
            updateValue={updateValue}
            renderLabelWithPrefix={renderLabelWithPrefix}
            fieldIsRequired={fieldIsRequired}
            validateSingleField={validateSingleField}
            validateVisible={validateVisible}
            setErrors={setErrors}
            setIsValid={setIsValid}
            onFormValidityChange={onFormValidityChange}
            submitted={submitted}
            errors={errors}
            countries_metaData={countries_metaData}
          />
        );
      }

      if (field.component === "state_selection") {
        return (
          <DynamicSelectIndianState
            key={field.name}
            field={field}
            fieldName={field.name}
            value={value}
            isMain={isMain}
            fieldRefs={fieldRefs}
            normalizeIndianStateOptions={normalizeIndianStateOptions}
            normalizeSelectValue={normalizeSelectValue}
            updateValue={updateValue}
            renderLabelWithPrefix={renderLabelWithPrefix}
            fieldIsRequired={fieldIsRequired}
            validateSingleField={validateSingleField}
            validateVisible={validateVisible}
            setErrors={setErrors}
            setIsValid={setIsValid}
            onFormValidityChange={onFormValidityChange}
            submitted={submitted}
            errors={errors}
            indian_states_metaData={indianStates}
          />
        );
      }

      /* ---------- YEAR PICKER SELECT ---------- */
      if (field.component === "yearpicker_select") {
        return (
          <DynamicSelectYearpicker
            key={field.name}
            field={field}
            fieldName={field.name}
            value={value}
            isMain={isMain}
            step={step}
            fieldRefs={fieldRefs}
            currentDOB={currentDOB}
            normalizeYearValues={normalizeYearValues}
            normalizeCurrentAndNextYear={normalizeCurrentAndNextYear}
            normalizeSelectValue={normalizeSelectValue}
            updateValue={updateValue}
            renderLabelWithPrefix={renderLabelWithPrefix}
            fieldIsRequired={fieldIsRequired}
            validateSingleField={validateSingleField}
            validateVisible={validateVisible}
            setErrors={setErrors}
            setIsValid={setIsValid}
            onFormValidityChange={onFormValidityChange}
            submitted={submitted}
            errors={errors}
          />
        );
      }

      /* ---------- MULTISELECT CHECKBOX ---------- */
      if (field.component === "multiselect_checkbox") {
        return (
          <DynamicCheckboxMultiselect
            key={field.name}
            field={field}
            fieldRefs={fieldRefs}
            value={value}
            isMain={isMain}
            step={step}
            submitted={submitted}
            errors={errors}
            updateValue={updateValue}
            renderLabelWithPrefix={renderLabelWithPrefix}
          />
        );
      }

      /* ---------- CHECKBOX ---------- */
      if (field.component === "checkbox") {
        return (
          <DynamicCheckbox
            key={field.name}
            field={field}
            fieldRefs={fieldRefs}
            value={value}
            isMain={isMain}
            step={step}
            submitted={submitted}
            errors={errors}
            updateValue={updateValue}
            renderLabelWithPrefix={renderLabelWithPrefix}
            renderField={renderField}
            resetSubtree={resetSubtree}
          />
        );
      }

      /* ---------- RADIO ---------- */

      if (field.component === "radio") {
        return (
          <DynamicChips
            key={field.name}
            field={field}
            fieldRefs={fieldRefs}
            value={value}
            isMain={isMain}
            step={step}
            submitted={submitted}
            errors={errors}
            updateValue={updateValue}
            renderLabelWithPrefix={renderLabelWithPrefix}
            renderField={renderField}
            resetSubtree={resetSubtree}
            safeState={safeState}
          />
        );
      }

      /* ---------- DATE PICKER ---------- */
      if (field.component === "datepicker") {
        return (
          <DynamicDatepicker
            key={field.name}
            field={field}
            fieldName={field.name}
            value={value}
            isMain={isMain}
            fieldRefs={fieldRefs}
            updateValue={updateValue}
            renderLabelWithPrefix={renderLabelWithPrefix}
            fieldIsRequired={fieldIsRequired}
            validateSingleField={validateSingleField}
            setErrors={setErrors}
            submitted={submitted}
            errors={errors}
          />
        );
      }

      /* ---------- MEDICATION MULTIPLE ---------- */
      if (field.component === "multiple_options") {
        return (
          <DynamicMedicationMultiple
            key={field.name}
            field={field}
            value={value}
            updateValue={updateValue}
            fieldRefs={fieldRefs}
            renderLabelWithPrefix={renderLabelWithPrefix}
            isMain={isMain}
            step={step}
            fieldIsRequired={fieldIsRequired}
            normalizeCountryOptions={normalizeCountryOptions}
            normalizeSelectValue={normalizeSelectValue}
            validateSingleField={validateSingleField}
            validateVisible={validateVisible}
            setErrors={setErrors}
            setIsValid={setIsValid}
            onFormValidityChange={onFormValidityChange}
            submitted={submitted}
            errors={errors}
            countries_metaData={countries_metaData}
          />
        );
      }

      return null;
    };

    useEffect(() => {
      // ===============================
      // INDIAN FLOW
      // ===============================
      if (nationality === "Indian") {
        if (residentialStatus === "Resident Indian") {
          if (residenceCountry !== "India") {
            handleChange("residenceCountry", "India");
          }
        }

        if (residentialStatus === "NRI") {
          if (residenceCountry === "India") {
            handleChange("residenceCountry", "");
          }
        }
      }

      // ===============================
      // NON INDIAN FLOW
      // ===============================
      if (nationality === "Non-Indian/OCI/PIO") {
        if (residentialStatus !== "Non-Indian/OCI/PIO") {
          handleChange("residentialStatus", "Non-Indian/OCI/PIO");
        }
      }
    }, [nationality, residentialStatus, residenceCountry, handleChange]);

    useEffect(() => {
      if (residencePincode?.length === 6 && stateResidence && cityResidence && pincodeAutoFilled.residence && !isUserNRI && !isUserNRI) {
        updatePincodeLock("residence", {
          state: true,
          city: true,
        });
      }

      if (workPincode?.length === 6 && stateWork && cityWork && pincodeAutoFilled.work && !isUserNRI && !isUserFN) {
        updatePincodeLock("work", {
          state: true,
          city: true,
        });
      }
    }, [
      residencePincode,
      stateResidence,
      cityResidence,
      workPincode,
      stateWork,
      cityWork,
      pincodeAutoFilled,
      isUserNRI,
      isUserFN,
      updatePincodeLock,
    ]);

    return (
      <Flex className="dfh-container" direction="column" gap="l">
        {schema.map((field, idx) => renderField(field, idx))}
      </Flex>
    );
  }
);

export default DynamicFormRenderer_Helix;
